import { makeStyles } from "tss-react/mui";
import {
  MIN_CARD_HEIGHT_PX,
  PRIORITY_SCHEDULER_SOURCE_TYPES,
} from "@miplatform/constants";
import { useDraggable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import {
  IPrioritySchedulerColumnProps,
  PrioritySchedulerColumnEvent,
} from "../PrioritySchedulerBoard.types";
import {
  getCardDnDId,
  isWorkOrderDragDisabled,
} from "../PrioritySchedulerBoard.utils";
import { PrioritySchedulerEventLayout } from "../eventLayouts.utils";

export interface DraggableEventCardProps {
  event: PrioritySchedulerColumnEvent;
  layout: PrioritySchedulerEventLayout;
  columnId: string;
  isHighlighted?: boolean;
  onEventRemove?: (entityId: string) => void;
  onCardClick: IPrioritySchedulerColumnProps["onCardClick"];
  EventComponent: IPrioritySchedulerColumnProps["EventComponent"];
  isMinimized?: boolean;
}

export function DraggableEventCard({
  event,
  layout,
  columnId,
  isHighlighted,
  onEventRemove,
  onCardClick,
  EventComponent,
  isMinimized,
}: Readonly<DraggableEventCardProps>) {
  const { classes, cx } = useStyles({
    top: layout.top,
    height: layout.height,
  });
  const isDragDisabled = isWorkOrderDragDisabled(event);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: getCardDnDId(event.id),
    disabled: isDragDisabled,
    data: {
      type: "card",
      cardId: event.id,
      layoutHeight: layout.height,
      container: { type: "column", id: columnId },
    },
  });

  return (
    <Box
      ref={setNodeRef}
      className={cx(
        classes.eventCard,
        event?.isGhost && classes.ghostEvent,
        isDragging && classes.draggingEvent,
        isMinimized && classes.minimizedEvent
      )}
      {...attributes}
      {...listeners}
    >
      {EventComponent && (
        <EventComponent
          attempt={event}
          isHighlighted={isHighlighted}
          onCardClick={onCardClick}
          onEventRemove={
            onEventRemove ? () => onEventRemove(event.id) : undefined
          }
          isRemovable={!event.assignedUserId}
          className={classes.eventContainer}
          source={{
            type: PRIORITY_SCHEDULER_SOURCE_TYPES.COLUMN,
            id: columnId,
          }}
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles<{ top: number; height: number }>()(
  (theme, { top, height }) => ({
    eventCard: {
      position: "absolute",
      top,
      left: theme.spacing(1),
      right: theme.spacing(1),
      height,
      zIndex: 1,
    },
    ghostEvent: {
      opacity: 0.5,
    },
    eventContainer: {
      width: "100%",
    },
    draggingEvent: {
      opacity: 0.45,
    },
    minimizedEvent: {
      height: MIN_CARD_HEIGHT_PX,
    },
  })
);
